import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTypeWire } from "@typewirets/react";
import { DeepResearchReportsAPIWire } from "@/lib/api/deep-research-reports.api.wire";
import { useAIFieldsStore } from "@/store/ai-fields.store";
import { useDeepResearchReportsStore } from "@/store/deep-research-reports.store";
import type {
  AIFieldDefinition,
  CreateAIFieldRequest,
  CreateReportRequest,
  CreateTasksResponse,
  DeepResearchReportMetadata,
  DeepResearchReportSummary,
  DeepResearchRowDetails,
  DeleteReportResponse,
  PopulateRecordsResponse,
  UpdateAIFieldRequest,
  UpdateReportRequest,
} from "@/types/deep-research-reports";

export const DEEP_RESEARCH_QUERY_KEY = ["deep-research"] as const;

/**
 * Hook for listing all deep research reports
 */
export function useDeepResearchReports() {
  const drrApi = useTypeWire(DeepResearchReportsAPIWire);
  return useQuery<DeepResearchReportSummary[]>({
    queryKey: DEEP_RESEARCH_QUERY_KEY,
    queryFn: async () => {
      return drrApi.listReports();
    },
  });
}

/**
 * Hook for fetching a single report's metadata
 */
export function useDeepResearchReport(reportId: string | undefined) {
  const drrApi = useTypeWire(DeepResearchReportsAPIWire);
  const updateOne = useDeepResearchReportsStore((state) => state.updateOne);
  return useQuery<DeepResearchReportMetadata>({
    queryKey: [...DEEP_RESEARCH_QUERY_KEY, reportId],
    queryFn: async () => {
      if (!reportId) throw new Error("Report ID is required");
      const data = await drrApi.fetchMetadata(reportId);
      updateOne(reportId, data);
      return data;
    },
    enabled: !!reportId,
  });
}

/**
 * Hook for creating a new deep research report
 */
export function useCreateDeepResearchReport() {
  const queryClient = useQueryClient();
  const drrApi = useTypeWire(DeepResearchReportsAPIWire);

  return useMutation<DeepResearchReportMetadata, Error, CreateReportRequest>({
    mutationFn: async (data) => {
      return drrApi.createReport(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEEP_RESEARCH_QUERY_KEY });
    },
  });
}

/**
 * Hook for updating a deep research report
 */
export function useUpdateDeepResearchReport() {
  const queryClient = useQueryClient();
  const drrApi = useTypeWire(DeepResearchReportsAPIWire);
  const updateOne = useDeepResearchReportsStore((state) => state.updateOne);

  return useMutation<DeepResearchReportMetadata, Error, UpdateReportRequest>({
    mutationFn: async (data) => {
      const { id } = data;
      const updated = await drrApi.updateReport(id, data);
      queryClient.setQueryData([...DEEP_RESEARCH_QUERY_KEY, id], updated);
      updateOne(id, updated);
      return updated;
    },
  });
}

/**
 * Hook for deleting a deep research report (draft only)
 */
export function useDeleteDeepResearchReport() {
  const queryClient = useQueryClient();
  const drrApi = useTypeWire(DeepResearchReportsAPIWire);

  return useMutation<DeleteReportResponse, Error, string>({
    mutationFn: async (reportId: string) => {
      return drrApi.deleteReport(reportId);
    },
    onSuccess: () => {
      // Invalidate the list query to refresh the data
      queryClient.invalidateQueries({ queryKey: DEEP_RESEARCH_QUERY_KEY });
    },
  });
}

/**
 * Hook for populating records list for a report (for review phase)
 */
export function usePopulateRecordsList() {
  const queryClient = useQueryClient();
  const drrApi = useTypeWire(DeepResearchReportsAPIWire);

  return useMutation<PopulateRecordsResponse, Error, string>({
    mutationFn: async (reportId: string) => {
      const data = await drrApi.populateRecordsList(reportId);
      queryClient.invalidateQueries({
        queryKey: [...DEEP_RESEARCH_QUERY_KEY, reportId],
      });
      queryClient.invalidateQueries({ queryKey: DEEP_RESEARCH_QUERY_KEY });
      return data;
    },
  });
}

/**
 * Hook for creating AI generation tasks for a report
 */
export function useCreateDeepResearchTasks() {
  const queryClient = useQueryClient();
  const drrApi = useTypeWire(DeepResearchReportsAPIWire);

  return useMutation<CreateTasksResponse, Error, string>({
    mutationFn: async (reportId: string) => {
      const data = await drrApi.createTasks(reportId);
      queryClient.invalidateQueries({
        queryKey: [...DEEP_RESEARCH_QUERY_KEY, reportId],
      });
      queryClient.invalidateQueries({ queryKey: DEEP_RESEARCH_QUERY_KEY });
      return data;
    },
  });
}

/**
 * Hook for listing AI field definitions for a specific entity type
 */
export function useAIFieldDefinitions(entityType: string) {
  const drrApi = useTypeWire(DeepResearchReportsAPIWire);
  const hydrate = useAIFieldsStore((state) => state.hydrate);
  const allFieldsById = useAIFieldsStore((state) => state.byId);
  const queryState = useQuery<AIFieldDefinition[]>({
    queryKey: [...DEEP_RESEARCH_QUERY_KEY, "ai-fields", entityType],
    queryFn: async () => {
      const fields = await drrApi.listAIFields(entityType);
      hydrate(fields);
      return fields;
    },
  });

  return {
    ...queryState,
    data: Object.values(allFieldsById).filter(
      (field) => field.entity_type === entityType,
    ),
  };
}

export function useCreateAIFieldDefinition() {
  const drrApi = useTypeWire(DeepResearchReportsAPIWire);
  const queryClient = useQueryClient();
  const updateOne = useAIFieldsStore((state) => state.updateOne);
  return useMutation<AIFieldDefinition, Error, CreateAIFieldRequest>({
    mutationFn: async (data) => {
      const newAIField = await drrApi.createAIField(data);
      updateOne(newAIField.id, newAIField);
      return newAIField;
    },
    onSuccess: (created) => {
      // Invalidate unified field definitions so new AI field appears in selectors/tables
      if (created?.entity_type) {
        const pluralEntity =
          created.entity_type === "account"
            ? "accounts"
            : created.entity_type === "opportunity"
              ? "opportunities"
              : (created.entity_type as string);
        queryClient.invalidateQueries({
          queryKey: ["field-definitions", pluralEntity],
        });
      }
    },
  });
}

export function useDeleteAIFieldDefinition() {
  const drrApi = useTypeWire(DeepResearchReportsAPIWire);
  const deleteById = useAIFieldsStore((state) => state.deleteById);
  return useMutation<void, Error, string>({
    mutationFn: async (fieldId: string) => {
      await drrApi.deleteAIField(fieldId);
      deleteById(fieldId);
    },
  });
}

/**
 * Hook for updating an AI field definition
 */
export function useUpdateAIFieldDefinition() {
  const drrApi = useTypeWire(DeepResearchReportsAPIWire);
  const updateOne = useAIFieldsStore((state) => state.updateOne);

  return useMutation<
    AIFieldDefinition,
    Error,
    { field_id: string; body: UpdateAIFieldRequest }
  >({
    mutationFn: async ({ field_id, body }) => {
      const updated = await drrApi.updateAIField(field_id, body);
      updateOne(field_id, updated);
      return updated;
    },
  });
}

/**
 * Hook for removing an AI field from a report (draft-only)
 */
export function useRemoveAIFieldFromReport() {
  const updateReport = useUpdateDeepResearchReport();
  const getReport = useDeepResearchReportsStore((state) => state.byId);

  return useMutation<
    DeepResearchReportMetadata,
    Error,
    {
      reportId: string;
      fieldId: string;
    }
  >({
    mutationFn: async ({ reportId, fieldId }) => {
      const current = getReport[reportId];
      if (!current) throw new Error("Report not found in store");
      const updatedIds = (current.ai_fields || [])
        .filter((f) => f.id !== fieldId)
        .map((f) => f.id);
      return updateReport.mutateAsync({
        id: reportId,
        visible_ai_field_ids: updatedIds,
      });
    },
  });
}

// Types for Deep Research Data Response (report rows endpoint)
export interface DeepResearchDataRow {
  entity_id: string;
  data: Record<string, unknown>;
  _metadata?: {
    added_at?: string;
  };
}

export interface DeepResearchDataResponse {
  results: Record<string, unknown>[];
  total: number;
  limit: number;
  offset: number;
}

export function useDeepResearchRowDetails(
  reportId: string | undefined,
  entityId: string | undefined,
  options?: { enabled?: boolean },
) {
  const drrApi = useTypeWire(DeepResearchReportsAPIWire);
  return useQuery<DeepResearchRowDetails>({
    queryKey: [...DEEP_RESEARCH_QUERY_KEY, reportId, "row-details", entityId],
    queryFn: async () => {
      if (!reportId || !entityId) throw new Error("IDs required");
      return drrApi.fetchRowDetails(reportId, entityId);
    },
    enabled: !!reportId && !!entityId && (options?.enabled ?? true),
  });
}

/**
 * Hook for fetching deep research report data (actual records with AI field values)
 * Now supports filtering, sorting, and field selection for submitted reports
 */
export function useDeepResearchData(
  reportId: string | undefined,
  options?: {
    limit?: number;
    offset?: number;
    // Query parameters for server-side filtering/sorting
    sort_by?: string;
    sort_direction?: "asc" | "desc";
    null_sort?: string;
    search?: string;
    filters?: string; // JSON string of filter structure
    extra_fields?: string; // Comma-separated list of field IDs
  },
) {
  const drrApi = useTypeWire(DeepResearchReportsAPIWire);

  return useQuery<DeepResearchDataResponse>({
    queryKey: [
      ...DEEP_RESEARCH_QUERY_KEY,
      reportId,
      "data",
      options?.limit,
      options?.offset,
      options?.sort_by,
      options?.sort_direction,
      options?.search,
      options?.filters,
      options?.extra_fields,
    ],
    queryFn: async () => {
      if (!reportId) throw new Error("Report ID is required");

      const response = await drrApi.fetchReportData(reportId, {
        limit: options?.limit,
        offset: options?.offset,
        sort_by: options?.sort_by,
        sort_direction: options?.sort_direction,
        null_sort: options?.null_sort,
        search: options?.search,
        filters: options?.filters,
        extra_fields: options?.extra_fields,
      });

      return response;
    },
    enabled: !!reportId,
  });
}
